import React, { useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { userService } from '../../services/api/userService';
import {
  auth,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
  getMultiFactorResolver,
  TotpMultiFactorGenerator,
} from '../../config/firebase';
import './PasswordChangeForm.css';

interface PasswordValidation {
  minLength: boolean;
  uppercase: boolean;
  lowercase: boolean;
  number: boolean;
  specialChar: boolean;
}

interface PasswordChangeFormProps {
  onPasswordChanged?: () => void;
  submitLabel?: string;
  changingLabel?: string;
}

const PasswordChangeForm: React.FC<PasswordChangeFormProps> = ({
  onPasswordChanged,
  submitLabel,
  changingLabel,
}) => {
  const { t } = useTranslation();

  // Password fields
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  // MFA verification modal state
  const [showMfaModal, setShowMfaModal] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaError, setMfaError] = useState('');
  const [mfaVerifying, setMfaVerifying] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mfaResolverRef = useRef<any>(null);

  // Password validation
  const validatePassword = useCallback((password: string): PasswordValidation => ({
    minLength: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    specialChar: /[^A-Za-z0-9]/.test(password),
  }), []);

  const validation = validatePassword(newPassword);
  const allRulesPass = Object.values(validation).every(Boolean);
  const passwordsMatch = newPassword === confirmPassword && confirmPassword.length > 0;
  const notSamePassword = newPassword !== currentPassword || currentPassword.length === 0;
  const canSubmit = allRulesPass && passwordsMatch && notSamePassword && currentPassword.length > 0 && !changingPassword;

  // Clear form after successful password change
  const clearPasswordForm = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  };

  // Complete the password update after reauthentication (with or without MFA)
  const completePasswordUpdate = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) return;

    await updatePassword(currentUser, newPassword);
    // Log the password change for audit
    try {
      await userService.logPasswordChange();
    } catch {
      // Audit log failure should not block the success flow
    }
    setPasswordSuccess(t('profile.passwordChangedSuccess'));
    clearPasswordForm();
    onPasswordChanged?.();
  };

  // Handle password change — reauthenticate, then update
  const handleChangePassword = async () => {
    setPasswordError('');
    setPasswordSuccess('');
    setChangingPassword(true);

    const currentUser = auth.currentUser;
    if (!currentUser || !currentUser.email) {
      setPasswordError(t('profile.errors.passwordChangeFailed'));
      setChangingPassword(false);
      return;
    }

    try {
      // Step 1: Reauthenticate with current password
      const credential = EmailAuthProvider.credential(currentUser.email, currentPassword);
      await reauthenticateWithCredential(currentUser, credential);

      // Step 2: If no MFA challenge, update password directly
      await completePasswordUpdate();
    } catch (error: any) {
      if (error.code === 'auth/multi-factor-auth-required') {
        // Step 3: MFA challenge — show TOTP verification modal
        const resolver = getMultiFactorResolver(auth, error);
        mfaResolverRef.current = resolver;
        setShowMfaModal(true);
        setMfaCode('');
        setMfaError('');
        // Don't set changingPassword to false — we're still in the flow
        return;
      }

      if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        setPasswordError(t('profile.errors.currentPasswordIncorrect'));
      } else if (error.code === 'auth/too-many-requests') {
        setPasswordError(t('profile.errors.passwordChangeFailed'));
      } else {
        setPasswordError(error.message || t('profile.errors.passwordChangeFailed'));
      }
    } finally {
      if (!mfaResolverRef.current) {
        setChangingPassword(false);
      }
    }
  };

  // MFA verification: resolve TOTP challenge
  const handleMfaVerify = async () => {
    const resolver = mfaResolverRef.current;
    if (!resolver) return;

    setMfaError('');
    setMfaVerifying(true);

    try {
      const hint = resolver.hints[0]; // TOTP factor
      const assertion = TotpMultiFactorGenerator.assertionForSignIn(hint.uid, mfaCode);
      await resolver.resolveSignIn(assertion);

      // MFA passed — now update the password
      setShowMfaModal(false);
      mfaResolverRef.current = null;

      await completePasswordUpdate();
    } catch (error: any) {
      if (error.code === 'auth/invalid-verification-code') {
        setMfaError(t('profile.mfa.invalidCode'));
      } else {
        setMfaError(error.message || t('profile.mfa.invalidCode'));
      }
    } finally {
      setMfaVerifying(false);
      setChangingPassword(false);
    }
  };

  const handleMfaCancel = () => {
    setShowMfaModal(false);
    setMfaCode('');
    setMfaError('');
    mfaResolverRef.current = null;
    setChangingPassword(false);
  };

  const ValidationCheck: React.FC<{ passed: boolean; label: string }> = ({ passed, label }) => (
    <div className={`validation-rule ${passed ? 'rule-pass' : 'rule-fail'}`}>
      <span className="rule-icon">{passed ? '\u2713' : '\u2717'}</span>
      <span className="rule-text">{label}</span>
    </div>
  );

  return (
    <>
      <div className="password-form">
        {/* Current Password */}
        <div className="password-input-group">
          <label className="password-input-label">{t('profile.currentPassword')}</label>
          <div className="password-input-wrapper">
            <input
              type={showCurrentPassword ? 'text' : 'password'}
              className="password-input"
              value={currentPassword}
              onChange={e => { setCurrentPassword(e.target.value); setPasswordError(''); }}
              placeholder={t('profile.currentPassword')}
              autoComplete="current-password"
            />
            <button
              type="button"
              className="password-toggle-btn"
              onClick={() => setShowCurrentPassword(!showCurrentPassword)}
              title={showCurrentPassword ? t('profile.hidePassword') : t('profile.showPassword')}
            >
              {showCurrentPassword ? '\u25C9' : '\u25CE'}
            </button>
          </div>
        </div>

        {/* New Password */}
        <div className="password-input-group">
          <label className="password-input-label">{t('profile.newPassword')}</label>
          <div className="password-input-wrapper">
            <input
              type={showNewPassword ? 'text' : 'password'}
              className="password-input"
              value={newPassword}
              onChange={e => { setNewPassword(e.target.value); setPasswordError(''); }}
              placeholder={t('profile.newPassword')}
              autoComplete="new-password"
            />
            <button
              type="button"
              className="password-toggle-btn"
              onClick={() => setShowNewPassword(!showNewPassword)}
              title={showNewPassword ? t('profile.hidePassword') : t('profile.showPassword')}
            >
              {showNewPassword ? '\u25C9' : '\u25CE'}
            </button>
          </div>
        </div>

        {/* Confirm Password */}
        <div className="password-input-group">
          <label className="password-input-label">{t('profile.confirmPassword')}</label>
          <div className="password-input-wrapper">
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              className="password-input"
              value={confirmPassword}
              onChange={e => { setConfirmPassword(e.target.value); setPasswordError(''); }}
              placeholder={t('profile.confirmPassword')}
              autoComplete="new-password"
            />
            <button
              type="button"
              className="password-toggle-btn"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              title={showConfirmPassword ? t('profile.hidePassword') : t('profile.showPassword')}
            >
              {showConfirmPassword ? '\u25C9' : '\u25CE'}
            </button>
          </div>
        </div>

        {/* Validation Checklist */}
        <div className="password-validation">
          <span className="validation-title">{t('profile.requirements.title')}</span>
          <ValidationCheck passed={validation.minLength} label={t('profile.requirements.minLength')} />
          <ValidationCheck passed={validation.uppercase} label={t('profile.requirements.uppercase')} />
          <ValidationCheck passed={validation.lowercase} label={t('profile.requirements.lowercase')} />
          <ValidationCheck passed={validation.number} label={t('profile.requirements.number')} />
          <ValidationCheck passed={validation.specialChar} label={t('profile.requirements.specialChar')} />
          {confirmPassword.length > 0 && (
            <ValidationCheck passed={passwordsMatch} label={t('profile.requirements.passwordsMatch')} />
          )}
          {newPassword.length > 0 && currentPassword.length > 0 && !notSamePassword && (
            <div className="validation-rule rule-fail">
              <span className="rule-icon">{'\u2717'}</span>
              <span className="rule-text">{t('profile.errors.samePassword')}</span>
            </div>
          )}
        </div>

        {/* Error / Success Messages */}
        {passwordError && <div className="password-message password-error">{passwordError}</div>}
        {passwordSuccess && <div className="password-message password-success">{passwordSuccess}</div>}

        {/* Submit Button */}
        <button
          className="password-submit-btn"
          onClick={handleChangePassword}
          disabled={!canSubmit}
        >
          {changingPassword
            ? (changingLabel || t('profile.changingPassword'))
            : (submitLabel || t('profile.changePasswordButton'))}
        </button>
      </div>

      {/* MFA Verification Modal */}
      {showMfaModal && (
        <div className="mfa-modal-backdrop" onClick={e => e.stopPropagation()}>
          <div className="mfa-modal">
            <h3 className="mfa-modal-title">{t('profile.mfa.title')}</h3>
            <p className="mfa-modal-prompt">{t('profile.mfa.prompt')}</p>
            <input
              type="text"
              className="mfa-code-input"
              value={mfaCode}
              onChange={e => {
                const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                setMfaCode(val);
                setMfaError('');
              }}
              placeholder="000000"
              maxLength={6}
              autoFocus
              onKeyDown={e => {
                if (e.key === 'Enter' && mfaCode.length === 6 && !mfaVerifying) {
                  handleMfaVerify();
                }
              }}
            />
            {mfaError && <div className="password-message password-error">{mfaError}</div>}
            <div className="mfa-modal-actions">
              <button className="mfa-cancel-btn" onClick={handleMfaCancel} disabled={mfaVerifying}>
                {t('profile.mfa.cancel')}
              </button>
              <button
                className="mfa-verify-btn"
                onClick={handleMfaVerify}
                disabled={mfaCode.length !== 6 || mfaVerifying}
              >
                {mfaVerifying ? '...' : t('profile.mfa.verify')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PasswordChangeForm;
